import fetch from "isomorphic-fetch";
import {
  schemaResponse,
  schemaRequestCompleted,
  autoloadingFormEdited,
  schemaAutoSuggestRequestCompleted,
  replaceCurrentUser,
} from "../schemaActions";
import { schemaError, unknownError, addError } from "../errorActions";
import ApiConfig from "../../config/ApiConfig";
import doNothing from "./successHandlers/doNothing";
import standardResponseHandler from "./standardResponseHandler";

export function doSchemaGet({ id, accessToken, endpoint }) {
  return makeCallWithOptions({
    verb: "GET",
    accessToken,
    endpoint,
    successHandler: (json, dispatch, id) =>
      replaceContainerData(json, dispatch, id),
    options: {
      id,
    },
  });
}

export function doAutoSuggestGet({ id, accessToken, endpoint, property }) {
  return makeCallWithOptions({
    verb: "GET",
    accessToken,
    endpoint,
    successHandler: getAutoSuggestSuccessful,
    options: {
      id,
      property,
    },
  });
}

export function doListDelete({
                               parentId,
                               accessToken,
                               endpoint,
                               listEndpoint,
                               navigate,
                             }) {
  return makeCallWithOptions({
    verb: "DELETE",
    accessToken,
    navigate,
    endpoint,
    successHandler: (json, dispatch) => {
      reinitializeGet(parentId, accessToken, listEndpoint, dispatch);
    },
    options: {
      id: parentId,
      castToJson: false,
    },
  });
}

export function doRedirectCreate({
                                   accessToken,
                                   endpoint,
                                   successRedirectUrl,
                                   payload,
                                   navigate,
                                   message,
                                 }) {
  return makeCallWithOptions({
    verb: "POST",
    accessToken,
    endpoint,
    successHandler: doNothing,
    payload,
    options: {
      castToJson: false,
      successMessage: message,
      navigate,
      successRedirectUrl,
    },
  });
}

export function callAndReplaceContainerData({
                                              accessToken,
                                              endpoint,
                                              castToJson,
                                              payload,
                                              verb,
                                              id,
                                            }) {
  return makeCallWithOptions({
    verb,
    accessToken,
    endpoint,
    payload,
    successHandler: (json, dispatch, id) =>
      replaceContainerData(json, dispatch, id),
    options: {
      id,
      castToJson,
    },
  });
}

export function doSchemaCreate({
                                 accessToken,
                                 endpoint,
                                 castToJson,
                                 payload,
                                 navigate,
                                 callback = doNothing,
                               }) {
  return makeCallWithOptions({
    verb: "POST",
    accessToken,
    endpoint,
    successHandler: callback,
    payload,
    options: {
      castToJson,
      navigate,
    },
  });
}

export function doSchemaUpdate({
                                 accessToken,
                                 endpoint,
                                 castToJson,
                                 payload,
                                 navigate,
                                 callback = doNothing,
                                 message = "",
                               }) {
  return makeCallWithOptions({
    verb: "PUT",
    accessToken,
    endpoint,
    successHandler: callback,
    payload,
    options: {
      castToJson,
      navigate,
      successMessage: message,
    },
  });
}

export function doRedirectFullUpdate({
                                       accessToken,
                                       endpoint,
                                       successRedirectUrl,
                                       payload,
                                       navigate,
                                       message = "",
                                       id = null,
                                     }) {
  return makeCallWithOptions({
    verb: "PUT",
    accessToken,
    endpoint,
    successHandler: doNothing,
    payload,
    options: {
      id,
      castToJson: false,
      successMessage: message,
      navigate,
      successRedirectUrl,
    },
  });
}

export function doRedirectDelete({
                                   accessToken,
                                   endpoint,
                                   successRedirectUrl,
                                   navigate,
                                   message = "",
                                 }) {
  return makeCallWithOptions({
    verb: "DELETE",
    accessToken,
    endpoint,
    successHandler: doNothing,
    options: {
      castToJson: false,
      successMessage: message,
      navigate,
      successRedirectUrl,
    },
  });
}

export function doRedirectUpdateOwnUser({
                                          payload,
                                          accessToken,
                                          endpoint,
                                          navigate,
                                          redirectUrl,
                                        }) {
  return makeCallWithOptions({
    verb: "POST",
    accessToken,
    endpoint,
    successHandler: (json, dispatch, id, property) =>
      updateOwnUser(json, dispatch, redirectUrl),
    payload,
    options: {
      navigate,
    },
  });
}

export function makeCallWithOptions({
                                      verb,
                                      accessToken,
                                      endpoint,
                                      errorHandler = getError,
                                      successHandler,
                                      payload,
                                      options: {
                                        navigate = null,
                                        successMessage = null,
                                        successRedirectUrl = null,
                                        id = null,
                                        castToJson = true,
                                        property = null,
                                        fetchOptions = null,
                                        refetchOptions = null,
                                      },
                                    }) {
  return function (dispatch) {
    const bodyJson = payload ? JSON.stringify(payload) : undefined;
    const fetchParameters = fetchOptions || {
      method: verb,
      mode: "cors",
      credentials: "include",
      headers: {
        Authorization: "Bearer " + accessToken,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: bodyJson,
    };

    return fetch(endpoint, fetchParameters)
      .then((response) => {
        standardResponseHandler({
          response,
          dispatch,
          errorHandler,
          successHandler,
          options: {
            navigate,
            successMessage,
            successRedirectUrl,
            id,
            castToJson,
            property,
          },
        });
      })
      .catch((error) => {
        dispatch(schemaRequestCompleted(id));
        dispatch(schemaError(error));
      });
  };
}

export function doFileUpload({ file, accessToken, endpoint, id, property }) {
  return function (dispatch) {
    let data = new FormData();
    data.append("file", file);
    return fetch(endpoint, {
      method: "POST",
      mode: "cors",
      credentials: "include",
      headers: {
        Authorization: "Bearer " + accessToken,
        Accept: "application/json",
      },
      body: data,
    })
      .then((response) => {
        standardResponseHandler({
          response,
          dispatch,
          errorHandler,
          successHandler: handleImageReplacement,
          options: {
            id,
            property,
          },
        });
      })
      .catch((error) => {
        console.error(error);
        dispatch(addError("Unable to upload file"));
      });
  };
}

function handleImageReplacement(json, dispatch, id, property) {
  const url = ApiConfig.protocol + ApiConfig.apiUri + json.uri;
  dispatch(autoloadingFormEdited(id, property, url));
}

function updateOwnUser(json, dispatch, navigate, redirectUrl) {
  if (json.id) {
    dispatch(replaceCurrentUser(json));
    navigate(redirectUrl);
  } else {
    dispatch(addError("Something went wrong"));
  }
}

function reinitializeGet(id, accessToken, endpoint, dispatch) {
  dispatch(doSchemaGet({ id, accessToken, endpoint }));
}

function errorHandler(error, dispatch) {
  dispatch(schemaError(error));
}

function getError(error, dispatch, id) {
  dispatch(schemaRequestCompleted(id));
  dispatch(schemaError(error));
}

export const replaceContainerData = (json, dispatch, id) => {
  dispatch(schemaRequestCompleted(id));
  dispatch(schemaResponse(json, id));
};

function getAutoSuggestSuccessful(json, dispatch, id, property) {
  dispatch(schemaAutoSuggestRequestCompleted(json, id, property));
  dispatch(schemaRequestCompleted(id));
}
